import React, {
  useContext,
  useEffect,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { BsBank2, BsCheckCircle } from "react-icons/bs";
import { PiCopy } from "react-icons/pi";
import "./index.scss";
import axios from "axios";
import { usePayDataContext } from "../../contexts/PayDataContext";
import { FaSpinner } from "react-icons/fa";
import Success from "../success";
import Waiting from "./waiting";
import Failed from "./failed";
import Expired from "./expired";
import { CopyToClipboard } from "react-copy-to-clipboard";
import {
  PaymentContext,
  PaymentContextValue,
  PaymentStage,
} from "../../contexts/Payment/payment.context";
import CountdownTimer from "../countdownTimer";
import { useViewContext } from "../../contexts/view.context";
import {
  formatCurrencyAmount,
  getFallbackRedirectUrl,
} from "../../utils/helpers";
import { RiErrorWarningFill } from "react-icons/ri";

const url = process.env.REACT_APP_API_URL;

const TransferForm: React.FC = () => {
  const {
    isLoading,
    accountData,
    merchantData,
    handleGenerateNewAccount,
    payReference,
    customerInfo,
  } = usePayDataContext();
  const { view, setView } = useViewContext();
  const [reference, setReference] = useState<string>("");
  const [referenceId, setReferenceId] = useState<string>("");
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [delay, setDelay] = useState<number>(5000);
  const [newTimer, setNewTimer] = useState<boolean>(true);
  const [copiedText, setCopiedText] = useState<string | null>(null);
  const {
    setPaymentReference,
    setResponseMessage,
    state,
  }: PaymentContextValue = useContext(PaymentContext);
  const [timerExpired, setTimerExpired] = useState(false);
  const [isMoneySent, setIsMoneySent] = useState(false);

  const { fullName, email } = customerInfo;

  const handleTimerExpired = () => {
    setTimerExpired(true);
    setView("expired");
  };

  useEffect(() => {
    setReference(payReference);
  }, [payReference]);

  useLayoutEffect(() => {
    if (
      state.responseMessage === "approved" ||
      state.responseMessage === "successful"
    ) {
      setView("success");
      return;
    }
  });

  const queryIntervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    queryIntervalRef.current = setInterval(() => {
      handleConfirmPayment();
    }, 30000);

    return () => {
      if (queryIntervalRef.current) {
        clearInterval(queryIntervalRef.current);
        queryIntervalRef.current = null;
      }
    };
  }, []);

  const clearQueryInterval = () => {
    if (queryIntervalRef.current) {
      clearInterval(queryIntervalRef.current);
      queryIntervalRef.current = null;
    }
  };

  const handleConfirmPayment = async () => {
    try {
      const requestBody = {
        reference: payReference,
        account_reference: accountData.account_reference,
        metadata: {
          name: fullName,
          email,
        },
      };

      const response = await axios.post(
        `${url}/v2/payment/confirm-account-payment`,
        requestBody
      );
      handleResponse(response.data);
    } catch (error) {
      clearQueryInterval();
      setView("failed");
    }
  };

  const handleResponse = (data) => {
    const { status, reference } = data.data;
    setResponseMessage(status);
    if (status === "approved" || status === "successful") {
      clearQueryInterval();
      setPaymentReference(reference);
      setView("success");

      const redirectTarget = getFallbackRedirectUrl(
        merchantData?.meta?.redirect_url,
        window.location.origin
      );

      if (redirectTarget) {
        setTimeout(() => {
          window.location.href = merchantData.meta.redirect_url;
          window.location.href = redirectTarget;
        }, 2000);
      }
      return;
    }

    if (status === "pending" && isMoneySent) {
      setView("waiting");
    }

    if (timerExpired) {
      clearQueryInterval();
      setView("failed");
    }
  };

  const handleCopy = (text: string) => {
    setCopiedText(text);

    setTimeout(() => {
      setCopiedText(null);
    }, 2000);
  };

  const handleRetryGenerate = () => {
    handleGenerateNewAccount();
  };

  const handleMoneySent = async () => {
    setIsMoneySent(true);
    setButtonLoading(true);
    try {
      await handleConfirmPayment();
    } catch (error) {
    } finally {
      setButtonLoading(false);
    }
  };

  return (
    <>
      {view === "transfer" && (
        <>
          {isLoading ? (
            <div className="flex justify-center items-center">
              <div className="h-72"></div>
              <div className="lds-spinner">
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
                <div></div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center mt-5">
              <p className="text-[var(--Checkout-text-color)] mb-4 text-xs Inter-Semibold">
                Transfer NGN {formatCurrencyAmount(merchantData?.amount)} to
                Local APM Checkout
              </p>

              <div className="flex gap-1 text-sm mb-2 py-1 px-3 rounded-[40px] text-[#EB5757] border-2 border-[#EB5757]">
                <RiErrorWarningFill className="flex text-xl" />
                Please ensure you transfer exactly NGN{" "}
                {formatCurrencyAmount(merchantData?.amount)}
              </div>

              <div className="p-6 bg-[var(--Checkout-surface-color)] border border-[#e4e7ec] w-full rounded-xl my-1 px-4">
                <div className="flex justify-between text-sm">
                  <div className="mb-2">
                    <p className="text-[0.7rem] text-[#727272] Inter-Regular">
                      BANK NAME
                    </p>
                    <p className="text-[var(--Checkout-text-color)] Inter-Semibold">
                      {accountData?.bank}
                    </p>
                  </div>
                  {/* <p className="text-[0.7rem] mt-2 text-[#004262] Inter-Semibold">
                    CHANGE BANK
                  </p> */}
                </div>
                <div className="flex justify-between text-sm">
                  <div className="mb-2">
                    <p className="text-[0.7rem] text-[#727272] Inter-Regular">
                      ACCOUNT NUMBER
                    </p>
                    <p className="text-[var(--Checkout-text-color)] Inter-Semibold">
                      {accountData?.account_number}
                    </p>
                  </div>
                  <CopyToClipboard
                    text={accountData?.account_number}
                    onCopy={() => handleCopy(accountData?.account_number)}
                  >
                    <div className="flex items-center">
                      {copiedText === accountData?.account_number ? (
                        <>
                          <BsCheckCircle className="text-green-500 text-2xl mr-1" />
                          <span className="text-green-500 text-xs Inter-Regular">
                            Copied!
                          </span>
                        </>
                      ) : (
                        <PiCopy className="text-[#727272] text-2xl cursor-pointer mt-1 hover:text-[var(--SarepayPrimary-color)]" />
                      )}
                    </div>
                  </CopyToClipboard>
                </div>

                <div className="flex justify-between text-sm">
                  <div className="mb-2">
                    <p className="text-[0.7rem] text-[#727272] Inter-Regular">
                      ACCOUNT NAME
                    </p>
                    <p className="text-[var(--Checkout-text-color)] Inter-Semibold">
                      {accountData?.account_name}
                    </p>
                  </div>
                </div>

                <div className="flex justify-between text-sm">
                  <div className="mb-2">
                    <p className="text-[0.7rem] text-[#727272] Inter-Regular">
                      AMOUNT
                    </p>
                    <p className="text-[var(--Checkout-text-color)] Inter-Semibold">
                      NGN {formatCurrencyAmount(merchantData?.amount)}
                    </p>
                  </div>
                  <CopyToClipboard
                    text={merchantData?.amount}
                    onCopy={() => handleCopy(merchantData?.amount)}
                  >
                    <div className="flex items-center">
                      {copiedText === merchantData?.amount ? (
                        <>
                          <BsCheckCircle className="text-green-500 text-2xl mr-1" />
                          <span className="text-green-500 text-xs Inter-Regular">
                            Copied!
                          </span>
                        </>
                      ) : (
                        <PiCopy className="text-[#727272] text-2xl cursor-pointer mt-1 hover:text-[var(--SarepayPrimary-color)]" />
                      )}
                    </div>
                  </CopyToClipboard>
                </div>
              </div>
              <p className="text-[#727272] text-xs my-2 Inter-Regular">
                Use this account for this transaction only
              </p>

              <div>
                <div className="relative">
                  {newTimer && (
                    <div className="lds-dual-ring absolute -top-2 left-[1.9rem]"></div>
                  )}
                  <div className="flex justify-center">
                  <BsBank2 className="text-xl my-4 text-[var(--Checkout-accent-color)]" />
                  </div>
                  <div className="w-32">
                    <CountdownTimer onTimerExpired={handleTimerExpired} />
                  </div>
                </div>
              </div>

              <div>
                <button
                  className="px-2 py-[0.4rem] w-64 border border-[#d0d5dd] bg-white flex items-center justify-center rounded-xl outline-none my-2 sm:w-[17rem] text-[#667085] hover:text-[var(--SarepayPrimary-color)] text-xs transition-colors"
                  onClick={handleMoneySent}
                  disabled={buttonLoading}
                  style={{
                    backgroundColor: buttonLoading ? "#b7b7b7" : "",
                    cursor: buttonLoading ? "not-allowed" : "pointer",
                  }}
                >
                  {!buttonLoading ? (
                    "I’ve sent the money"
                  ) : (
                    <div className="flex items-center">
                      {" "}
                      <FaSpinner className="animate-spin mr-2" />{" "}
                      <p className="Inter-Regular">I’ve sent the money</p>
                    </div>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {view === "success" && (
        <div className="my-4">
          <Success />
        </div>
      )}
      {view === "waiting" && (
        <div className="my-4">
          <Waiting />
        </div>
      )}

      {view === "failed" && (
        <div className="my-4">
          <Failed />
        </div>
      )}

      {view === "expired" && (
        <div className="my-4">
          <Expired
            handleConfirmPayment={handleConfirmPayment}
            buttonLoading={buttonLoading}
            handleRetryGenerate={handleRetryGenerate}
          />
        </div>
      )}
    </>
  );
};

export default TransferForm;
